/**
 * AWARE Health — AlertBadge Component
 * Badge for alert levels. In simple view, shows human-friendly labels.
 * In detailed view, shows technical labels. Theme-aware.
 */

import { useTranslation } from 'react-i18next'
import { useViewMode } from '../../contexts/ViewModeContext'
import type { AlertLevel } from '../../types/forecast'

interface AlertBadgeProps {
  level: AlertLevel
  size?: 'sm' | 'md' | 'lg'
}

const CONFIG: Record<AlertLevel, { color: string; bg: string; icon: string; pulse: boolean }> = {
  None:      { color: '#64748b', bg: 'rgba(100,116,139,0.12)', icon: '●',  pulse: false },
  Watch:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: '◆',  pulse: false },
  Warning:   { color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   icon: '▲',  pulse: false },
  Emergency: { color: '#a855f7', bg: 'rgba(168,85,247,0.15)',  icon: '⚠',  pulse: true  },
}

// Map AlertLevel to i18n key suffix
const ALERT_KEY_MAP: Record<AlertLevel, string> = {
  None: 'none',
  Watch: 'watch',
  Warning: 'warning',
  Emergency: 'emergency',
}

export default function AlertBadge({ level, size = 'md' }: AlertBadgeProps) {
  const { t } = useTranslation()
  const { isDetailed } = useViewMode()
  const cfg = CONFIG[level] ?? CONFIG['None']
  const key = ALERT_KEY_MAP[level] ?? 'none'

  // Simple view: "All Clear", "Stay Informed", etc.
  // Detailed view: "No Alert", "Watch", "Warning", "Emergency"
  const label = isDetailed
    ? t(`alert.${key}Detailed`)
    : t(`alert.${key}`)

  const sizeStyles = {
    sm: { padding: '3px 8px',  fontSize: '10px', gap: 4 },
    md: { padding: '4px 12px', fontSize: '11px', gap: 5 },
    lg: { padding: '6px 14px', fontSize: '13px', gap: 6 },
  }[size]

  return (
    <span
      className={cfg.pulse ? 'animate-glow-pulse' : ''}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizeStyles.gap,
        padding: sizeStyles.padding,
        borderRadius: 20,
        fontSize: sizeStyles.fontSize,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}44`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: sizeStyles.fontSize }}>{cfg.icon}</span>
      {label}
    </span>
  )
}
